import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Star, Instagram, Phone } from "lucide-react";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { ABOUT, BUSINESS } from "../data";

export default function AboutPage() {
  const navigate = useNavigate();
  const handleBook = () => navigate("/#booking");

  return (
    <div className="dark grain bg-[#050505] min-h-screen" data-testid="about-page">
      <Navbar onBook={handleBook} />

      <section className="relative pt-32 md:pt-40 pb-24 md:pb-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <button
            data-testid="about-back"
            onClick={() => navigate("/")}
            className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-zinc-500 hover:text-white transition-colors mb-12"
          >
            <ArrowLeft size={14} /> Back
          </button>

          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="font-heading text-5xl sm:text-6xl lg:text-7xl font-extrabold tracking-tighter text-white leading-[0.9] uppercase"
          >
            {ABOUT.heading}
          </motion.h1>

          <div className="mt-14 grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* Image with rating badge */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="relative"
            >
              <div className="relative rounded-3xl overflow-hidden border border-white/10 aspect-[4/5] max-w-md">
                <img
                  src={ABOUT.image}
                  alt="Marez Detailing founder work"
                  className="absolute inset-0 w-full h-full object-cover object-center"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#050505]/60 to-transparent" />
                <div
                  data-testid="about-rating"
                  className="absolute bottom-5 right-5 rounded-2xl border border-white/15 bg-black/60 backdrop-blur-md px-5 py-4 text-center"
                >
                  <div className="flex items-center gap-1.5">
                    <Star size={16} className="fill-white text-white" />
                    <span className="font-heading text-2xl font-bold text-white">
                      {ABOUT.rating}
                    </span>
                  </div>
                  <p className="mt-1 font-mono text-[9px] uppercase tracking-[0.2em] text-zinc-400">
                    {ABOUT.ratingLabel}
                  </p>
                </div>
              </div>
            </motion.div>

            {/* Copy */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <div className="space-y-5">
                {ABOUT.paragraphs.map((p, i) => (
                  <p key={i} className="text-base md:text-lg text-zinc-300 leading-relaxed">
                    {p}
                  </p>
                ))}
              </div>

              <button
                data-testid="about-book-btn"
                onClick={handleBook}
                className="btn-3d gloss-sweep group mt-9 inline-flex items-center gap-2 rounded-full bg-[#fafafa] text-[#050505] font-semibold px-8 py-4 hover:bg-white"
              >
                Book With Me
                <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform duration-300" />
              </button>

              <div className="mt-8 flex flex-wrap items-center gap-6">
                <a
                  href={`tel:${BUSINESS.phoneRaw}`}
                  className="flex items-center gap-2 text-zinc-400 hover:text-white transition-colors"
                >
                  <Phone size={16} /> {BUSINESS.phone}
                </a>
                <a
                  href={BUSINESS.instagramUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 text-zinc-400 hover:text-white transition-colors"
                >
                  <Instagram size={16} /> @{BUSINESS.instagram}
                </a>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
